import React, { useEffect, useMemo, useRef, useState } from "react";
import { MagnifyingGlass, ArrowRight, Star } from "@phosphor-icons/react";
import { baseNavItems, ADMIN_NAV_ITEM } from "@/lib/navConfig";
import { VA_FORMS } from "@/lib/vaFormsCatalog";
import { getPrefs, toggleFavorite } from "@/lib/prefs";

/**
 * Global command palette. Opens with ⌘K / Ctrl-K from anywhere, or by clicking
 * the search bar in the top header. Type to filter nav items + VA forms by
 * fuzzy match. Enter or click jumps to the destination.
 *
 *  - Nav matches set `activeView` via the `onNavigate` callback.
 *  - Form matches open the PDF in a new tab.
 *  - Star icon on each nav row pins/unpins the item to the sidebar favorites.
 */
export default function CommandPalette({ open, onClose, onNavigate, isAdmin }) {
  const [query, setQuery] = useState("");
  const [cursor, setCursor] = useState(0);
  const [, forceRerender] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (open) {
      setQuery("");
      setCursor(0);
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  const navPool = useMemo(() => {
    const items = [...baseNavItems];
    if (isAdmin) items.push(ADMIN_NAV_ITEM);
    return items.map((n) => ({ kind: "nav", id: n.id, title: n.label, sub: "Jump to view" }));
  }, [isAdmin]);

  const formPool = useMemo(() => VA_FORMS.map((f) => ({
    kind: "form",
    id: `form:${f.num}`,
    title: `${f.num} — ${f.title}`,
    sub: f.blurb,
    pdf: f.pdf,
    info: f.info,
    tags: f.tags || [],
  })), []);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      // Empty state — show navigation first, then a handful of common forms.
      return [...navPool, ...formPool.slice(0, 6)];
    }
    const tokens = q.split(/\s+/).filter(Boolean);
    function matches(rec) {
      const hay = `${rec.title} ${rec.sub || ""} ${(rec.tags || []).join(" ")}`.toLowerCase();
      return tokens.every((t) => hay.includes(t));
    }
    return [...navPool.filter(matches), ...formPool.filter(matches)].slice(0, 25);
  }, [query, navPool, formPool]);

  useEffect(() => { setCursor(0); }, [query]);

  function pickResult(idx) {
    const r = results[idx];
    if (!r) return;
    if (r.kind === "nav") {
      onNavigate(r.id);
    } else if (r.kind === "form") {
      window.open(r.pdf, "_blank", "noopener,noreferrer");
    }
    onClose();
  }

  function handleKey(e) {
    if (e.key === "ArrowDown") { e.preventDefault(); setCursor((c) => Math.min(c + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setCursor((c) => Math.max(c - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); pickResult(cursor); }
    else if (e.key === "Escape") { e.preventDefault(); onClose(); }
  }

  // Scroll the cursor into view as the user arrows through results.
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-idx="${cursor}"]`);
    if (el?.scrollIntoView) el.scrollIntoView({ block: "nearest" });
  }, [cursor]);

  if (!open) return null;

  const favorites = getPrefs().favorites;

  return (
    <div
      className="cmdk-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label="Quick search and navigation"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="cmdk-backdrop"
    >
      <div className="cmdk-panel" data-testid="cmdk-panel" onKeyDown={handleKey}>
        <div className="cmdk-input-row">
          <MagnifyingGlass size={18} weight="bold" className="cmdk-icon" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search views, VA forms, statements..."
            className="cmdk-input"
            data-testid="cmdk-input"
            autoComplete="off"
            spellCheck={false}
          />
          <kbd className="cmdk-kbd">ESC</kbd>
        </div>
        <div className="cmdk-results" ref={listRef} data-testid="cmdk-results">
          {results.length === 0 && (
            <div className="cmdk-empty">No matches for &quot;{query}&quot;.</div>
          )}
          {results.map((r, idx) => {
            const isFav = r.kind === "nav" && favorites.includes(r.id);
            return (
              <button
                key={r.id}
                data-idx={idx}
                data-testid={`cmdk-row-${r.id}`}
                onClick={() => pickResult(idx)}
                onMouseEnter={() => setCursor(idx)}
                className={`cmdk-row ${idx === cursor ? "cmdk-row--active" : ""}`}
              >
                <span className={`cmdk-row-kind cmdk-row-kind--${r.kind}`}>
                  {r.kind === "nav" ? "VIEW" : "FORM"}
                </span>
                <span className="cmdk-row-text">
                  <span className="cmdk-row-title">{r.title}</span>
                  {r.sub && <span className="cmdk-row-sub">{r.sub}</span>}
                </span>
                {r.kind === "nav" && (
                  <button
                    type="button"
                    aria-label={isFav ? "Unpin from favorites" : "Pin to favorites"}
                    className="cmdk-row-fav"
                    onClick={(e) => { e.stopPropagation(); toggleFavorite(r.id); forceRerender((n) => n + 1); }}
                    data-testid={`cmdk-fav-${r.id}`}
                  >
                    <Star size={14} weight={isFav ? "fill" : "regular"} color={isFav ? "var(--gold-500)" : "var(--text-tertiary)"} />
                  </button>
                )}
                <ArrowRight size={13} weight="bold" className="cmdk-row-arrow" />
              </button>
            );
          })}
        </div>
        <div className="cmdk-footer">
          <span><kbd>↑</kbd><kbd>↓</kbd> navigate</span>
          <span><kbd>↵</kbd> open</span>
          <span><kbd>★</kbd> pin to sidebar</span>
        </div>
      </div>
    </div>
  );
}
